import { Directive, OnDestroy } from '@angular/core';
import { Observable, Subject, defer } from 'rxjs';
import { debounceTime, defaultIfEmpty, distinctUntilChanged, filter, finalize, map, take, takeUntil } from 'rxjs/operators';

export interface AirportPage {
  results: any[];
  count: number;
}

/** Owns paged search timing, completion and recovery for each picker. */
@Directive()
export abstract class AirportSearchBase implements OnDestroy {
  airports: any[] = [];
  airportsLoading = false;
  airportsPage = 0;
  airportsPageSize = 30;
  airportsHasMore = false;
  airportSearchTerm = '';
  airportSearch$ = new Subject<string>();
  protected destroy$ = new Subject<void>();

  private pending = false;
  private failed = false;
  private lastQueried = '';
  private lastQueriedRevision = -1;
  private searchRevision = 0;
  private disposed = false;
  private readonly cleared$ = new Subject<void>();

  /** Includes the debounce window; appending a page keeps existing rows visible. */
  get airportSearchPending(): boolean { return this.pending; }
  get airportSearchFailed(): boolean { return this.failed; }

  /** One page per read. Empty completion is an empty page; errors remain failures. */
  protected abstract fetchAirports(params: { page: number; page_size: number; search?: string }): Observable<AirportPage>;

  protected getPreselectedAirport(): any { return null; }

  protected buildAirportList(reset: boolean, results: any[]): any[] {
    if (!reset) { return [...this.airports, ...results]; }
    const preselected = this.getPreselectedAirport();
    if (preselected && !results.some((a: any) => a.id === preselected.id)) {
      return [preselected, ...results];
    }
    return results;
  }

  initAirportSearch(): void {
    this.airportSearch$.pipe(
      map(term => ({ term, revision: this.searchRevision })),
      debounceTime(250),
      filter(request => request.revision === this.searchRevision),
      distinctUntilChanged((previous, current) =>
        previous.term === current.term && previous.revision === current.revision),
      takeUntil(this.destroy$)
    ).subscribe(({ term, revision }) => {
      this.airportSearchTerm = term ?? '';
      // Clearing invalidates queued terms; an immediate retry in the new
      // revision must not be sent again when its debounce finishes.
      if (term === this.lastQueried && revision === this.lastQueriedRevision) { return; }
      this.airportsPage = 0;
      this.airportsHasMore = false;
      this.loadAirports(true, this.airportSearchTerm);
    });
  }

  onAirportSearch(term: string): void {
    this.airportSearchTerm = term?.trim() ?? '';
    const repeated = !!this.airportSearchTerm && this.airportSearchTerm === this.lastQueried;
    this.pending = !!this.airportSearchTerm && (!repeated || !this.airports.length || this.airportsLoading);
    this.airportsPage = 0;
    this.airportsHasMore = false;
    this.airportSearch$.next(this.airportSearchTerm);

    // A cleared or empty result must be searchable again even when the
    // debounced stream suppresses an unchanged term.
    if (repeated && !this.airports.length) {
      this.loadAirports(true, this.airportSearchTerm);
    }
  }

  /** Clear a selection/dismissal, including any pending or outstanding read. */
  clearAirportSearch(): void {
    this.searchRevision++;
    this.cleared$.next();
    this.airports = [];
    this.airportSearchTerm = '';
    this.airportsPage = 0;
    this.airportsHasMore = false;
    this.pending = false;
    this.failed = false;
  }

  onAirportDropdownOpen(): void {
    this.loadAirports(true, this.airportSearchTerm);
  }

  onAirportScrollToEnd(): void {
    if (this.airportsHasMore) { this.loadAirports(false, this.airportSearchTerm); }
  }

  loadAirports(reset: boolean, search: string): void {
    if (this.disposed || this.airportsLoading) { return; }
    if (!reset && !this.airportsHasMore) { return; }
    this.airportsLoading = true;
    if (reset) { this.pending = true; }
    this.lastQueried = search;
    this.lastQueriedRevision = this.searchRevision;
    const nextPage = reset ? 1 : this.airportsPage + 1;
    const revision = this.searchRevision;
    let settled = false;
    defer(() => this.fetchAirports({ page: nextPage, page_size: this.airportsPageSize, search: search || undefined })).pipe(
      // Some existing adapters toast and complete without emitting a page.
      defaultIfEmpty({ results: [], count: 0 }),
      take(1),
      finalize(() => {
        this.airportsLoading = false;
        this.pending = false;
        if (settled && revision === this.searchRevision && reset && this.airportSearchTerm !== search) {
          this.loadAirports(true, this.airportSearchTerm);
        }
      }),
      takeUntil(this.cleared$),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (response) => {
        settled = true;
        this.failed = false;
        this.airports = this.buildAirportList(reset, response.results);
        this.airportsPage = nextPage;
        this.airportsHasMore = this.airports.length < response.count;
      },
      error: () => {
        settled = true;
        this.failed = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.disposed = true;
    this.searchRevision++;
    this.destroy$.next();
    this.destroy$.complete();
  }
}
